import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSession } from '../context/SessionContext';
import { useAuth } from '../context/AuthContext';
import { Match } from '../types';
import { ResultsSkeleton } from '../components/Skeleton';
import { updateSessionHistory } from '../utils/sessionHistory';
import { restaurantApi, favoriteApi, Branch } from '../services/api';
import { pluralize } from '../utils/pluralize';
import { CategoryPlaceholder, LocationPinIcon, MapIcon, PhoneIcon, GlobeIcon } from '../components/Icons';
import WorkingHoursBadge from '../components/WorkingHoursBadge';
import Button from '../components/Button';
import PageTransition from '../components/PageTransition';
import SessionProgress from '../components/SessionProgress';
import ProfileButton from '../components/ProfileButton';

const ResultsPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { matches, loadMatches } = useSession();
  const { isAuthenticated, requestAuth } = useAuth();

  const [initialLoading, setInitialLoading] = useState(true);
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set());
  const [randomPick, setRandomPick] = useState<Match | null>(null);
  const [spinning, setSpinning] = useState(false);
  const [spinMatch, setSpinMatch] = useState<Match | null>(null);
  const spinTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const matchCardRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Branch expansion state
  const [expandedChain, setExpandedChain] = useState<string | null>(null);
  const [branchesData, setBranchesData] = useState<Record<string, Branch[]>>({});
  const [loadingBranches, setLoadingBranches] = useState<string | null>(null);

  useEffect(() => {
    if (sessionId) {
      loadMatches(sessionId).then(() => setInitialLoading(false));
    }
  }, [sessionId, loadMatches]);

  // Save to session history when matches load
  useEffect(() => {
    if (sessionId && matches.length >= 0) {
      updateSessionHistory(sessionId, matches.length);
    }
  }, [sessionId, matches.length]);

  // Load favorites
  useEffect(() => {
    if (isAuthenticated) {
      favoriteApi.list().then((data) => {
        setFavoriteIds(new Set(data.restaurantIds));
      }).catch(() => {});
    } else {
      setFavoriteIds(new Set());
    }
  }, [isAuthenticated]);

  const toggleFavorite = async (restaurantId: string) => {
    if (!isAuthenticated) {
      const result = await requestAuth();
      if (!result) return;
      try {
        const data = await favoriteApi.list();
        setFavoriteIds(new Set(data.restaurantIds));
      } catch {}
      return;
    }

    const isFav = favoriteIds.has(restaurantId);
    setFavoriteIds((prev) => {
      const next = new Set(prev);
      if (isFav) next.delete(restaurantId);
      else next.add(restaurantId);
      return next;
    });

    try {
      if (isFav) await favoriteApi.remove(restaurantId);
      else await favoriteApi.add(restaurantId);
    } catch {
      setFavoriteIds((prev) => {
        const next = new Set(prev);
        if (isFav) next.add(restaurantId);
        else next.delete(restaurantId);
        return next;
      });
    }
  };

  const startSpin = useCallback(() => {
    if (matches.length < 2 || spinning) return;

    setRandomPick(null);
    setSpinMatch(null);
    setSpinning(true);

    let count = 0;
    const totalSteps = 15 + Math.floor(Math.random() * 10);
    const finalIndex = Math.floor(Math.random() * matches.length);

    const tick = () => {
      count++;
      const idx = count % matches.length;
      setSpinMatch(matches[idx]);

      if (count >= totalSteps) {
        setSpinMatch(matches[finalIndex]);
        setRandomPick(matches[finalIndex]);
        setSpinning(false);
        return;
      }

      const delay = count < totalSteps - 5 ? 80 : 80 + (count - (totalSteps - 5)) * 50;
      spinTimer.current = setTimeout(tick, delay);
    };

    tick();

    return () => {
      if (spinTimer.current) clearTimeout(spinTimer.current);
    };
  }, [matches, spinning]);

  // Скролл к выбранной карточке после окончания спина
  useEffect(() => {
    if (!randomPick || spinning) return;
    const id = randomPick.restaurant?.id;
    if (!id) return;
    setTimeout(() => {
      matchCardRefs.current.get(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
  }, [randomPick, spinning]);

  const addAllToFavorites = useCallback(async () => {
    if (!isAuthenticated) {
      const result = await requestAuth();
      if (!result) return;
    }
    const ids = matches
      .filter(m => m.restaurant?.restaurantId)
      .map(m => m.restaurant!.restaurantId!);
    await Promise.all(ids.map(id => favoriteApi.add(id).catch(() => {})));
    setFavoriteIds(prev => new Set(Array.from(prev).concat(ids)));
  }, [matches, isAuthenticated, requestAuth]);

  const toggleBranches = useCallback(async (chainName: string) => {
    if (expandedChain === chainName) {
      setExpandedChain(null);
      return;
    }

    setExpandedChain(chainName);

    if (!branchesData[chainName]) {
      setLoadingBranches(chainName);
      try {
        const { branches } = await restaurantApi.getBranches(chainName);
        setBranchesData((prev) => ({ ...prev, [chainName]: branches }));
      } catch (err) {
        console.error('Failed to load branches:', err);
      } finally {
        setLoadingBranches(null);
      }
    }
  }, [expandedChain, branchesData]);

  const isChain = (match: Match) =>
    match.restaurant?.chainName && match.restaurant?.branchCount && match.restaurant.branchCount > 1;

  const headerBar = (
    <div className="bg-white border-b border-gray-100 px-4 sticky top-0 z-10">
      <div className="max-w-md mx-auto flex items-center justify-between py-3">
        <button onClick={() => navigate('/')} className="text-xl font-display font-bold text-gray-900 hover:opacity-70 transition-opacity">
          Swipe<span className="text-orange-500">Eat</span>
        </button>
        <ProfileButton />
      </div>
    </div>
  );

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50 flex flex-col">
        {headerBar}
        <div className="px-4 py-8"><ResultsSkeleton /></div>
      </div>
    );
  }

  return (
    <PageTransition className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50 flex flex-col">
      {headerBar}
      <div className="px-4 py-6">
      <div className="max-w-md mx-auto">

        {/* Заголовок + прогресс */}
        <div className="flex items-center gap-3 mb-5">
          <h1 className="text-2xl font-body font-bold text-gray-900">Результаты</h1>
        </div>
        <SessionProgress step={4} />

        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-sm text-gray-500">
              {matches.length > 0 ? `Совпадений: ${matches.length}` : 'Совпадений пока нет'}
            </p>
          </div>
          {matches.length > 0 && (
            <Button variant="outline" size="xs" onClick={addAllToFavorites} className="flex-shrink-0">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
              Все в избранное
            </Button>
          )}
        </div>

        {matches.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 mb-4 rounded-full bg-gray-100 flex items-center justify-center mx-auto">
              <svg className="w-8 h-8 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              Нет совпадений
            </h3>
            <p className="text-gray-500 mb-6">
              К сожалению, не нашлось заведения, которое понравилось бы всем.
            </p>
            <Button onClick={() => navigate('/create')}>
              Попробовать снова
            </Button>
          </div>
        ) : (
          <>
            {/* Randomizer */}
            {matches.length > 1 && (
              <div className="mb-6">
                <div className="bg-white rounded-2xl shadow-md overflow-hidden">
                  {/* Spinning / selected card */}
                  {(spinning || randomPick) && (() => {
                    const current = spinning ? spinMatch : randomPick;
                    return (
                      <div className={`transition-all duration-100 ${spinning ? 'opacity-75' : 'opacity-100'}`}>
                        <CategoryPlaceholder
                          cuisine={current?.restaurant?.cuisineTags || current?.restaurant?.cuisine || ''}
                          className="h-28 w-full"
                          iconClassName="w-14 h-14"
                        />
                        <div className="px-4 pt-3 pb-1">
                          {spinning && (
                            <p className="text-[10px] font-medium text-orange-400 uppercase tracking-wide mb-0.5">Выбираем...</p>
                          )}
                          {!spinning && (
                            <p className="text-[10px] font-medium text-green-500 uppercase tracking-wide mb-0.5">Выбор пал на!</p>
                          )}
                          <p className={`font-bold text-base line-clamp-1 ${spinning ? 'text-orange-500 animate-pulse' : 'text-gray-900'}`}>
                            {current?.restaurant?.name}
                          </p>
                          {current?.restaurant?.cuisineTags && (
                            <p className="text-xs text-orange-600">{current.restaurant.cuisineTags}</p>
                          )}
                          {!spinning && current?.restaurant?.averageCheck && (
                            <p className="text-xs text-gray-500 mt-0.5">{current.restaurant.averageCheck}</p>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Button */}
                  <div className="p-4 pt-3">
                    {!spinning && !randomPick && (
                      <p className="text-sm text-gray-400 text-center mb-3">Не можете выбрать? Поможем!</p>
                    )}
                    <Button fullWidth disabled={spinning} onClick={startSpin}>
                      {spinning ? 'Выбираем...' : randomPick ? 'Крутить ещё раз' : 'Выбрать случайно'}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Match list */}
            <div className="space-y-4">
              {matches.map((match, index) => {
                const isHighlighted = randomPick?.restaurant?.id === match.restaurant?.id;
                const isDimmed = randomPick && !isHighlighted;
                const chainName = match.restaurant?.chainName;
                const hasChain = isChain(match);
                const isExpanded = hasChain && expandedChain === chainName;
                const branches = chainName ? branchesData[chainName] : undefined;

                return (
                  <div
                    key={index}
                    ref={(el) => {
                      const id = match.restaurant?.id;
                      if (id) {
                        if (el) matchCardRefs.current.set(id, el);
                        else matchCardRefs.current.delete(id);
                      }
                    }}
                    className={`bg-white rounded-2xl shadow-md overflow-hidden border-2 transition-all ${
                      isHighlighted
                        ? 'border-orange-500 ring-2 ring-orange-200 scale-[1.02]'
                        : isDimmed
                        ? 'border-transparent opacity-40'
                        : 'border-transparent'
                    }`}
                  >
                    {/* Photo */}
                    <div className="relative h-36 sm:h-40">
                      {match.restaurant?.photoUrl ? (
                        <img
                          src={match.restaurant.photoUrl}
                          alt={match.restaurant.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <CategoryPlaceholder
                          cuisine={match.restaurant?.cuisineTags || match.restaurant?.cuisine || ''}
                          className="w-full h-full"
                          iconClassName="w-16 h-16"
                        />
                      )}
                      <div className="absolute top-3 left-3 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                        {isHighlighted ? 'Идём сюда!' : 'Совпадение!'}
                      </div>
                      <div className="absolute top-3 right-3 flex items-center gap-1.5">
                        {hasChain && (
                          <span className="bg-blue-500/90 text-white px-2 py-1 rounded-full text-xs font-bold font-body">
                            {pluralize(match.restaurant!.branchCount!, 'филиал', 'филиала', 'филиалов')}
                          </span>
                        )}
                        {match.restaurant?.rating && (
                          <span className="bg-white/90 px-2 py-1 rounded-full text-sm font-bold flex items-center gap-1">
                            <span className="text-yellow-500">★</span> {match.restaurant.rating}
                          </span>
                        )}
                        {match.restaurant?.restaurantId && (
                          <button
                            onClick={() => toggleFavorite(match.restaurant!.restaurantId!)}
                            className="bg-white/90 w-8 h-8 rounded-full flex items-center justify-center hover:bg-white transition-colors"
                          >
                            <svg className={`w-4 h-4 ${favoriteIds.has(match.restaurant!.restaurantId!) ? 'text-red-500' : 'text-gray-400'}`} viewBox="0 0 24 24" fill={favoriteIds.has(match.restaurant!.restaurantId!) ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Info */}
                    <div className="p-4">
                      <h3 className="text-lg font-bold text-gray-900 mb-1">
                        {match.restaurant?.name}
                      </h3>
                      {match.restaurant?.cuisineTags && (
                        <p className="text-sm text-orange-600 font-medium mb-1">
                          {match.restaurant.cuisineTags}
                        </p>
                      )}
                      {match.restaurant?.cuisine && !match.restaurant?.cuisineTags && (
                        <p className="text-sm text-orange-600 font-medium mb-1">
                          {match.restaurant.cuisine}
                        </p>
                      )}
                      {match.restaurant?.averageCheck && (
                        <p className="text-xs text-gray-400 mb-1">{match.restaurant.averageCheck}</p>
                      )}
                      <p className="text-sm text-gray-500 mb-1 flex items-center gap-1">
                        <LocationPinIcon className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" />
                        {match.restaurant?.address}
                      </p>
                      <WorkingHoursBadge workingHours={match.restaurant?.workingHours} className="mb-2" />

                      {/* Feature chips */}
                      {match.restaurant?.features && (() => {
                        const HIGHLIGHT = ['Доставка', 'Бизнес-ланч', 'Wi-Fi', 'Детское меню', 'Завтраки', 'Можно с собакой'];
                        const present = HIGHLIGHT.filter(f => match.restaurant!.features!.includes(f));
                        return present.length > 0 ? (
                          <div className="flex flex-wrap gap-1 mb-3">
                            {present.map(f => (
                              <span key={f} className="px-2 py-0.5 bg-green-50 text-green-700 rounded-full text-[10px] font-medium">
                                {f}
                              </span>
                            ))}
                          </div>
                        ) : <div className="mb-3" />;
                      })()}

                      {/* Action buttons */}
                      <div className="flex gap-2">
                        {match.restaurant?.phone && (
                          <Button variant="success" size="sm" fullWidth href={`tel:${match.restaurant.phone}`}>
                            <PhoneIcon className="w-4 h-4" />
                            Позвонить
                          </Button>
                        )}
                        {match.restaurant?.restaurantId && (
                          <Button variant="info" size="sm" fullWidth href={`https://2gis.ru/search/${encodeURIComponent(match.restaurant.name)}`} target="_blank" rel="noopener noreferrer">
                            <MapIcon className="w-4 h-4" />
                            На карте
                          </Button>
                        )}
                        {match.restaurant?.website && (
                          <Button variant="outline" size="sm" fullWidth href={match.restaurant.website} target="_blank" rel="noopener noreferrer">
                            <GlobeIcon className="w-4 h-4" />
                            Сайт
                          </Button>
                        )}
                      </div>

                      {/* Expand branches button */}
                      {hasChain && chainName && (
                        <Button
                          variant="info-outline"
                          size="sm"
                          fullWidth
                          className="mt-3"
                          onClick={() => toggleBranches(chainName)}
                        >
                          <svg className={`w-3.5 h-3.5 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                          {isExpanded ? 'Скрыть филиалы' : `Все ${pluralize(match.restaurant!.branchCount!, 'филиал', 'филиала', 'филиалов')}`}
                        </Button>
                      )}
                    </div>

                    {/* Branches list */}
                    {isExpanded && chainName && (
                      <div className="border-t border-gray-100 bg-gray-50 px-4 py-3 animate-page-enter">
                        {loadingBranches === chainName ? (
                          <div className="text-center text-sm text-gray-400 py-2">Загрузка...</div>
                        ) : branches && branches.length > 0 ? (
                          <div className="space-y-2.5">
                            {branches.map((branch) => (
                              <div
                                key={branch.id}
                                className="bg-white rounded-xl p-3 shadow-sm"
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm text-gray-700 flex items-center gap-1">
                                      <LocationPinIcon className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" />
                                      <span className="truncate">{branch.address}</span>
                                    </p>
                                    {branch.averageCheck && (
                                      <p className="text-xs text-green-700 mt-0.5">{branch.averageCheck}</p>
                                    )}
                                    <WorkingHoursBadge workingHours={branch.workingHours} className="mt-0.5" />
                                  </div>
                                  <div className="flex items-center gap-1.5 flex-shrink-0">
                                    {branch.rating && (
                                      <span className="text-xs text-gray-600 flex items-center gap-0.5">
                                        <span className="text-yellow-500">★</span>{branch.rating}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex gap-2 mt-2">
                                  {branch.phone && (
                                    <Button variant="success" size="xs" fullWidth href={`tel:${branch.phone}`}>
                                      <PhoneIcon className="w-3 h-3" />
                                      Позвонить
                                    </Button>
                                  )}
                                  <Button variant="info" size="xs" fullWidth href={`https://2gis.ru/search/${encodeURIComponent(branch.name + ' ' + branch.address)}`} target="_blank" rel="noopener noreferrer">
                                    <MapIcon className="w-3 h-3" />
                                    На карте
                                  </Button>
                                  {branch.website && (
                                    <Button variant="outline" size="xs" fullWidth href={branch.website} target="_blank" rel="noopener noreferrer">
                                      <GlobeIcon className="w-3 h-3" />
                                      Сайт
                                    </Button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center text-sm text-gray-400 py-2">Филиалы не найдены</div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Back button */}
        <div className="mt-8 flex justify-center">
          <Button variant="outline" onClick={() => navigate('/')}>
            Вернуться на главную
          </Button>
        </div>
      </div>
      </div>
    </PageTransition>
  );
};

export default ResultsPage;
